/**
 * The final report's standard text in Teslyn Cloud (decision 885; docs/specs/project-reporting/final-report.md §4;
 * table `final_report_text`, migration 20261030120000): one row for the company (no project) and one for each project
 * with its own. A project's own is removed with deleted_at (the laptops then print the company's) and written again on
 * the same row. The database decides who may write (tenant admins, managers, data processors) and stamps who saved it.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import type { FinalReportText, FinalReportTextRow, ProjectFigureSources } from '@teslyn/domain';

const COLUMNS =
  'id, tenant_id, project_id, sections, equipment, changed_by_name, created_at, updated_at, updated_by, deleted_at';

export interface FinalReportTextApi {
  /** The company's row (projectId null) or the project's own, removed or not; null when never written. */
  load(tenantId: string, projectId: string | null): Promise<FinalReportTextRow | null>;
  /** Save the text on that row (brought back if it was removed), or a new row. */
  save(
    tenantId: string,
    projectId: string | null,
    existing: FinalReportTextRow | null,
    text: FinalReportText,
  ): Promise<FinalReportTextRow>;
  /** Remove a project's own text: the project prints the company's again. */
  remove(id: string): Promise<FinalReportTextRow>;
  /**
   * The project's QC settings the final report's figures come from (decision 898: its thorium limits and QC limits,
   * the same values Teslyn's QC uses); null when the project cannot be read.
   */
  projectQc(projectId: string): Promise<ProjectFigureSources['project'] | null>;
}

export function finalReportTextApi(sb: SupabaseClient): FinalReportTextApi {
  return {
    async load(tenantId, projectId) {
      const q = sb.from('final_report_text').select(COLUMNS).eq('tenant_id', tenantId);
      const r = await (
        projectId === null ? q.is('project_id', null) : q.eq('project_id', projectId)
      ).maybeSingle();
      if (r.error) throw new Error(r.error.message);
      return (r.data as FinalReportTextRow | null) ?? null;
    },
    async save(tenantId, projectId, existing, text) {
      const values = { sections: text.sections, equipment: text.equipment, deleted_at: null };
      const r = existing
        ? await sb.from('final_report_text').update(values).eq('id', existing.id).select(COLUMNS).single()
        : await sb
            .from('final_report_text')
            .insert({ tenant_id: tenantId, project_id: projectId, ...values })
            .select(COLUMNS)
            .single();
      if (r.error) throw new Error(`The standard text was not saved: ${r.error.message}`);
      return r.data as FinalReportTextRow;
    },
    async projectQc(projectId) {
      const r = await sb
        .from('project')
        .select('qc_hand_sample_pct, qc_test_line_pct, qc_rules')
        .eq('id', projectId)
        .maybeSingle();
      if (r.error) throw new Error(r.error.message);
      return (r.data as ProjectFigureSources['project'] | null) ?? null;
    },
    async remove(id) {
      const r = await sb
        .from('final_report_text')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id)
        .select(COLUMNS)
        .single();
      if (r.error) throw new Error(`The project's own text was not removed: ${r.error.message}`);
      return r.data as FinalReportTextRow;
    },
  };
}
